import Swiper, {Autoplay, EffectCoverflow, EffectFade, Navigation, Pagination} from 'swiper';
import GLightbox from "glightbox";

document.addEventListener("DOMContentLoaded", function (event) {

    // INIT MENUBUTTON
    const menu_button = document.querySelector('[data-behaviour="toggle-menu"]');
    menu_button.addEventListener('click', () => {
        document.body.classList.toggle('body--show');
    })

    // INIT NAV SUBMENUS
    const closeSubmenu = (item) => {
        item.classList.remove('nav_item--open');
        item.querySelector('[data-behaviour="toggle-submenu"]').setAttribute('aria-expanded', 'false');
    }

    const submenu_items = document.querySelectorAll('.nav_item--has-submenu');
    submenu_items.forEach((item) => {
        const toggle = item.querySelector('[data-behaviour="toggle-submenu"]');

        toggle.addEventListener('click', () => {
            const isOpen = item.classList.contains('nav_item--open');

            submenu_items.forEach((otherItem) => {
                if (otherItem !== item) {
                    closeSubmenu(otherItem);
                }
            });

            item.classList.toggle('nav_item--open', !isOpen);
            toggle.setAttribute('aria-expanded', String(!isOpen));
        });

        item.addEventListener('keydown', (event) => {
            if (event.key === 'Escape' && item.classList.contains('nav_item--open')) {
                closeSubmenu(item);
                toggle.focus();
            }
        });
    });

    document.addEventListener('click', (event) => {
        submenu_items.forEach((item) => {
            if (!item.contains(event.target)) {
                closeSubmenu(item);
            }
        });
    });

    document.addEventListener('focusin', (event) => {
        submenu_items.forEach((item) => {
            if (!item.contains(event.target)) {
                closeSubmenu(item);
            }
        });
    });

    // INIT LIGHTBOX
    const lightbox = GLightbox({
        selector: '.slider-gallery_lightbox-link',
        touchNavigation: true,
        loop: true,
    });

    // INIT INLINE VIDEO PLAYBACK
    const videoContainers = document.querySelectorAll('.section_video[data-hls-src]');
    videoContainers.forEach((container) => {
        const trigger = container.querySelector('.video-inline-trigger');
        if (!trigger) {
            return;
        }

        trigger.addEventListener('click', async () => {
            const hlsSrc = container.dataset.hlsSrc;
            if (!hlsSrc) {
                return;
            }

            const video = document.createElement('video');
            video.className = 'section_video_player';
            video.controls = true;
            video.playsInline = true;
            video.autoplay = true;

            // Carry over the thumbnail's aspect ratio so the video renders at the
            // right size immediately, instead of the browser's placeholder ratio
            // until the real video metadata has loaded (which causes a visible jump).
            const thumbnail = container.querySelector('img');
            if (thumbnail && thumbnail.naturalWidth && thumbnail.naturalHeight) {
                video.style.aspectRatio = `${thumbnail.naturalWidth} / ${thumbnail.naturalHeight}`;
            }

            container.innerHTML = '';
            container.appendChild(video);

            if (video.canPlayType('application/vnd.apple.mpegurl')) {
                video.src = hlsSrc;
            } else {
                const {default: Hls} = await import('hls.js');
                if (Hls.isSupported()) {
                    const hls = new Hls();
                    hls.loadSource(hlsSrc);
                    hls.attachMedia(video);
                }
            }

            video.play();
        });
    });

    // INIT AUDIO PLAYER
    const formatAudioTime = (seconds) => {
        if (!isFinite(seconds) || seconds < 0) {
            seconds = 0;
        }
        const total = Math.floor(seconds);
        const mins = Math.floor(total / 60);
        const secs = total % 60;
        return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    };

    const audioPlayers = document.querySelectorAll('[data-audio-player]');
    audioPlayers.forEach((player) => {
        const audio = player.querySelector('.audio-player_source');
        const toggle = player.querySelector('[data-audio-toggle]');
        const progress = player.querySelector('[data-audio-progress]');
        const progressFill = player.querySelector('[data-audio-progress-fill]');
        const timeLabel = player.querySelector('[data-audio-time]');

        if (!audio || !toggle || !progress || !progressFill || !timeLabel) {
            return;
        }

        const renderTime = () => {
            if (audio.paused && audio.currentTime === 0) {
                timeLabel.textContent = formatAudioTime(audio.duration);
            } else {
                timeLabel.textContent = formatAudioTime(audio.currentTime);
            }
        };

        const renderProgress = () => {
            const ratio = audio.duration ? audio.currentTime / audio.duration : 0;
            progressFill.style.width = `${ratio * 100}%`;
            progress.setAttribute('aria-valuenow', String(Math.round(audio.currentTime)));
        };

        const setPlayingState = (isPlaying) => {
            player.classList.toggle('audio-player--playing', isPlaying);
            toggle.setAttribute('aria-label', isPlaying ? 'Audio pausieren' : 'Audio abspielen');
        };

        const seekToClientX = (clientX) => {
            if (!audio.duration) {
                return;
            }
            const rect = progress.getBoundingClientRect();
            const ratio = Math.min(Math.max((clientX - rect.left) / rect.width, 0), 1);
            audio.currentTime = ratio * audio.duration;
            renderProgress();
            renderTime();
        };

        audio.addEventListener('loadedmetadata', () => {
            progress.setAttribute('aria-valuemax', String(Math.round(audio.duration)));
            renderTime();
        });

        audio.addEventListener('timeupdate', () => {
            renderProgress();
            renderTime();
        });

        audio.addEventListener('play', () => setPlayingState(true));
        audio.addEventListener('pause', () => setPlayingState(false));

        audio.addEventListener('ended', () => {
            audio.currentTime = 0;
            setPlayingState(false);
            renderProgress();
            renderTime();
        });

        toggle.addEventListener('click', () => {
            if (audio.paused) {
                audio.play();
            } else {
                audio.pause();
            }
        });

        progress.addEventListener('click', (event) => seekToClientX(event.clientX));

        progress.addEventListener('keydown', (event) => {
            if (!audio.duration) {
                return;
            }
            if (event.key === 'ArrowRight' || event.key === 'ArrowUp') {
                event.preventDefault();
                audio.currentTime = Math.min(audio.currentTime + 5, audio.duration);
            } else if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') {
                event.preventDefault();
                audio.currentTime = Math.max(audio.currentTime - 5, 0);
            } else if (event.key === ' ' || event.key === 'Enter') {
                event.preventDefault();
                audio.paused ? audio.play() : audio.pause();
                return;
            } else {
                return;
            }
            renderProgress();
            renderTime();
        });
    });

    // INIT SWIPER
    const sliders = document.querySelectorAll('.swiper');
    sliders.forEach(function (slider) {
        const autoSwiper = slider.classList.contains('swiper--auto');
        const horizontalPeek = slider.classList.contains('swiper--horizontal-peek');
        const horizontalFixed = slider.classList.contains('swiper--horizontal-fixed');

        const swiper = new Swiper(slider, {
            // configure Swiper to use modules
            modules: [Pagination, Navigation, Autoplay, EffectFade],
            effect: 'slide',
            fadeEffect: {
                crossFade: true
            },
            direction: (horizontalPeek || horizontalFixed) ? 'horizontal' : 'vertical',
            loop: true,

            slidesPerView: horizontalPeek ? 1.15 : 1,
            centeredSlides: horizontalPeek,
            spaceBetween: horizontalPeek ? 20 : 0,
            breakpoints: horizontalPeek ? {
                700: {
                    slidesPerView: 1.3,
                    spaceBetween: 24,
                },
                1000: {
                    slidesPerView: 1.5,
                    spaceBetween: 30,
                },
                1600: {
                    slidesPerView: 1.7,
                    spaceBetween: 30,
                },
            } : undefined,

            autoplay: autoSwiper ? {
                delay: 5000,
            } : false,

            // Navigation arrows
            navigation: {
                nextEl: slider.querySelector('.swiper-button-next'),
                prevEl: slider.querySelector('.swiper-button-prev'),
            },

            pagination: {
                el: slider.querySelector('.swiper-pagination'),
                type: 'bullets',
                clickable: true,
            },

            on: horizontalPeek ? {
                init: updateActiveSlideWidth,
                resize: updateActiveSlideWidth,
                breakpoint: updateActiveSlideWidth,
            } : {},
        });

        function updateActiveSlideWidth() {
            const activeSlide = slider.querySelector('.swiper-slide-active');
            if (activeSlide) {
                slider.style.setProperty('--swiper-active-width', `${activeSlide.offsetWidth}px`);
            }
        }
    });

    // FIXED HEADER
    window.addEventListener('scroll', () => {
        if (document.documentElement.scrollTop > 30 || document.body.scrollTop > 30){
            document.body.classList.add('menu--fixed');
        } else {
            document.body.classList.remove('menu--fixed');
        }
    });
});
